import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { venueUrl } from '@/lib/urls';
import { motion } from 'framer-motion';
import { 
  Calendar, MapPin, Users, Star, Clock, ArrowLeft, Ticket, 
  Share2, ChevronLeft, ChevronRight, Music, Utensils, Wine,
  PartyPopper, Check, Minus, Plus, Building2, Heart, Play,
  Mail, ExternalLink, CalendarPlus, ShieldCheck, CreditCard
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import ConsumerLayout from '@/components/ConsumerLayout';
import { SocialShare } from '@/components/SocialShare';
import ContactOrganiserModal from '@/components/ContactOrganiserModal';
import { formatDate, formatTime } from '@/lib/dateUtils';
import api from '@/lib/api';
import { toast } from 'sonner';

const HIGHLIGHT_ICONS = {
  'Live Music': Music,
  'Food & Drinks': Utensils,
  'Craft Beer': Wine,
  'Entertainment': PartyPopper,
  'VIP Access': Star,
};

// Gallery & Media Section - Auto-displays prominently
const GallerySection = ({ images, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  
  const placeholderImages = [
    'https://images.unsplash.com/photo-1766353846635-00a10ab84157?crop=entropy&cs=srgb&fm=jpg&q=85'
  ];
  
  const displayImages = images && images.length > 0 ? images : placeholderImages;
  const thumbnails = displayImages.slice(0, 4);

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
        <Play className="w-5 h-5 text-indigo-600" />
        Gallery & Media
      </h3>
      
      {/* Main Image/Video Display */}
      <div className="relative aspect-video rounded-xl overflow-hidden group bg-gray-900">
        <img
          src={displayImages[currentIndex]}
          alt={`${title} - Image ${currentIndex + 1}`}
          className="w-full h-full object-cover"
        />
        
        {/* Play Button Overlay for video feel */}
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
          <button 
            className="w-20 h-20 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-all hover:scale-110"
            onClick={() => setIsPlaying(true)}
          >
            <Play className="w-8 h-8 text-indigo-600 ml-1" fill="currentColor" />
          </button>
        </div>
        
        {/* Navigation Arrows */}
        {displayImages.length > 1 && (
          <>
            <button
              onClick={() => setCurrentIndex((prev) => (prev - 1 + displayImages.length) % displayImages.length)}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={() => setCurrentIndex((prev) => (prev + 1) % displayImages.length)}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
        
        {/* Live Badge */}
        <div className="absolute top-4 left-4">
          <Badge className="bg-indigo-600 text-white text-xs px-3 py-1">
            EVENT★GOERS LIVE
          </Badge>
        </div>
      </div>
      
      {/* Thumbnail Strip */}
      {displayImages.length > 1 && (
        <div className="flex gap-2">
          {thumbnails.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`relative flex-1 aspect-video rounded-lg overflow-hidden border-2 transition-all ${
                idx === currentIndex ? 'border-indigo-600 ring-2 ring-indigo-600/30' : 'border-transparent hover:border-indigo-400'
              }`}
            >
              <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Ticket Type Card - Industry Standard Design
const TicketTypeCard = ({ ticket, quantity, onQuantityChange, isGold }) => {
  const displayPrice = isGold && ticket.gold_price ? ticket.gold_price : ticket.price;
  const hasSavings = isGold && ticket.gold_price && ticket.gold_price < ticket.price;
  
  return (
    <div className="border rounded-xl p-4 hover:border-indigo-500/50 transition-all bg-white dark:bg-gray-900">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-grow">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-bold text-foreground">{ticket.name}</h4>
            {ticket.isVip && (
              <Badge className="bg-yellow-400 text-yellow-900 text-xs">
                <Star className="w-3 h-3 mr-1" fill="currentColor" />
                VIP
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground mb-2">{ticket.description}</p>
          {ticket.includes && ticket.includes.length > 0 && (
            <ul className="space-y-1">
              {ticket.includes.map((item, idx) => (
                <li key={idx} className="text-xs text-muted-foreground flex items-center gap-2">
                  <Check className="w-3 h-3 text-green-500" />
                  {item}
                </li>
              ))}
            </ul>
          )}
        </div>
        
        <div className="text-right flex-shrink-0">
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold text-indigo-600">${displayPrice}</span>
            {hasSavings && (
              <span className="text-sm text-muted-foreground line-through">${ticket.price}</span>
            )}
          </div>
          {hasSavings && (
            <Badge className="bg-yellow-400/20 text-yellow-700 dark:text-yellow-400 text-xs mt-1">
              Gold Price
            </Badge>
          )}
        </div>
      </div>
      
      {/* Quantity Controls */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onQuantityChange(ticket.id, Math.max(0, quantity - 1))}
            disabled={quantity <= 0}
          >
            <Minus className="w-4 h-4" />
          </Button>
          <span className="font-bold text-lg w-8 text-center">{quantity}</span>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onQuantityChange(ticket.id, quantity + 1)}
            disabled={quantity >= (ticket.available || 10)}
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        
        {quantity > 0 && (
          <span className="text-sm font-medium">
            Subtotal: ${(displayPrice * quantity).toFixed(2)}
          </span>
        )}
      </div>
    </div>
  );
};

// Modern Ticket Booking Widget with Ticket Options
const TicketBookingWidget = ({ event, isGold, isAuthenticated, onProceedToCheckout, venue }) => {
  const [ticketQuantities, setTicketQuantities] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  
  // Generate ticket types from event data
  const ticketTypes = event.ticket_types && event.ticket_types.length > 0
    ? event.ticket_types.map((t, i) => ({
        id: t.id || `ticket-${i}`,
        name: t.name || 'General Admission',
        description: t.description || '',
        price: t.price || event.price || 0,
        gold_price: t.gold_price,
        available: t.quantity || event.capacity || 100,
        includes: t.includes || [],
        isVip: t.is_vip || false,
      }))
    : [
      {
        id: 'general',
        name: 'General Admission',
        description: '',
        price: event.price || 0,
        gold_price: event.gold_price,
        available: event.capacity - (event.tickets_sold || 0),
        includes: ['Entry to event']
      },
      ...(event.vip_price ? [{
        id: 'vip',
        name: 'VIP',
        description: 'Premium experience',
        price: event.vip_price,
        gold_price: event.vip_gold_price,
        available: event.vip_capacity || 20,
        isVip: true,
        includes: ['Priority entry', 'VIP seating']
      }] : [])
    ];
  
  const handleQuantityChange = (ticketId, quantity) => {
    setTicketQuantities(prev => ({
      ...prev,
      [ticketId]: quantity
    }));
  };
  
  const totalTickets = Object.values(ticketQuantities).reduce((sum, qty) => sum + qty, 0);
  const totalPrice = ticketTypes.reduce((sum, ticket) => {
    const qty = ticketQuantities[ticket.id] || 0;
    const price = isGold && ticket.gold_price ? ticket.gold_price : ticket.price;
    return sum + (price * qty);
  }, 0);
  
  const handleBuyTickets = async () => {
    if (totalTickets === 0) {
      toast.error('Please select at least one ticket');
      return;
    }
    
    // Navigate to branded checkout page with total quantity
    const qty = totalTickets;
    navigate(`/checkout?type=event&id=${event.id}&qty=${qty}`);
  };
  
  const startingPrice = Math.min(...ticketTypes.map(t => isGold && t.gold_price ? t.gold_price : t.price));
  const availableTickets = event.capacity - (event.tickets_sold || 0);
  const isSoldOut = availableTickets <= 0;

  return (
    <Card className="border-2 shadow-xl overflow-hidden" data-testid="ticket-booking-widget">
      {/* Header with Starting Price */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 text-center">
        <p className="text-sm uppercase tracking-wider opacity-90 mb-1">Tickets Starting From</p>
        <p className="text-5xl font-bold">${startingPrice}</p>
      </div>
      
      <CardContent className="p-6 space-y-6">
        {/* Date & Time */}
        <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <Clock className="w-5 h-5 text-indigo-600" />
          <div>
            <p className="font-semibold text-foreground">{formatDate(event.date)}</p>
            <p className="text-sm text-muted-foreground">{formatTime(event.time)}</p>
          </div>
          <button 
            className="ml-auto text-indigo-600 hover:text-indigo-700 text-sm font-medium flex items-center gap-1"
            onClick={() => {
              const title = encodeURIComponent(event.title || 'Event');
              const startDate = event.date ? event.date.replace(/-/g, '') : '';
              const startTime = event.time ? event.time.replace(/[: ]/g, '').replace(/(AM|PM)/i, '') : '1900';
              // Build Google Calendar URL
              const gcalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDate}T${startTime}00/${startDate}T235900&location=${encodeURIComponent(event.venue_name || '')}`;
              window.open(gcalUrl, '_blank');
            }}
            data-testid="add-to-calendar-btn"
          >
            <CalendarPlus className="w-4 h-4" />
            Add to Calendar
          </button>
        </div>
        
        {/* Select Tickets Section */}
        <div>
          <h4 className="font-bold text-foreground mb-4 uppercase text-sm tracking-wider">
            Select Tickets
          </h4>
          <div className="space-y-3">
            {ticketTypes.map((ticket) => (
              <TicketTypeCard
                key={ticket.id}
                ticket={ticket}
                quantity={ticketQuantities[ticket.id] || 0}
                onQuantityChange={handleQuantityChange}
                isGold={isGold}
              />
            ))}
          </div>
        </div>
        
        {/* Total Summary */}
        {totalTickets > 0 && (
          <div className="p-4 bg-indigo-50 dark:bg-indigo-950/30 rounded-lg">
            <div className="flex justify-between items-center mb-2">
              <span className="text-muted-foreground">{totalTickets} ticket{totalTickets > 1 ? 's' : ''}</span>
              <span className="text-2xl font-bold text-indigo-600">${totalPrice.toFixed(2)}</span>
            </div>
          </div>
        )}
        
        {/* Buy Button */}
        <Button
          className="w-full h-14 bg-green-600 hover:bg-green-700 text-white font-bold text-lg"
          onClick={handleBuyTickets}
          disabled={loading || isSoldOut || totalTickets === 0}
          data-testid="buy-tickets-btn"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              Processing...
            </span>
          ) : isSoldOut ? (
            'Sold Out'
          ) : totalTickets === 0 ? (
            'Select Tickets'
          ) : (
            <>
              <Ticket className="w-5 h-5 mr-2" />
              Buy Tickets
            </>
          )}
        </Button>
        
        {/* Security & Trust Badges */}
        <div className="space-y-3 pt-2">
          <div className="flex items-center gap-2 text-sm text-green-600">
            <Check className="w-4 h-4" />
            <span>Instant Confirmation via Email</span>
          </div>
          
          <div className="border-t pt-3">
            <p className="text-xs text-muted-foreground text-center mb-2 uppercase tracking-wider">
              Safe & Secure Payment
            </p>
            <div className="flex items-center justify-center gap-3 opacity-60">
              <CreditCard className="w-8 h-8" />
              <ShieldCheck className="w-6 h-6" />
            </div>
          </div>
        </div>
        
        <p className="text-xs text-center text-muted-foreground">
          Secure checkout powered by Eventgoers
        </p>
      </CardContent>
    </Card>
  );
};

// Location Section with Map
const LocationSection = ({ venue, event }) => {
  const address = venue?.address || event?.venue_address || 'RiNo Art District, Denver, CO';
  const venueName = venue?.name || event?.venue_name || 'Event Venue';
  
  // Generate Google Maps embed URL
  const mapQuery = encodeURIComponent(`${venueName}, ${address}`);
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${mapQuery}`;
  
  // Embed URL for iframe (using Maps Embed API)
  const embedUrl = `https://www.google.com/maps/embed/v1/place?key=AIzaSyCHJPkfGCpXIT0225976P3tGS2rcL2xI_g&q=${mapQuery}`;
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-foreground">Location</h3>
      
      {/* Map Embed */}
      <div className="relative aspect-video bg-gray-200 dark:bg-gray-800 rounded-xl overflow-hidden">
        <iframe
          src={embedUrl}
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Venue Location"
        />
        
        {/* Open in Maps Overlay Button */}
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute bottom-4 right-4 bg-white dark:bg-gray-900 shadow-lg rounded-lg px-4 py-2 flex items-center gap-2 hover:shadow-xl transition-shadow"
        >
          <MapPin className="w-4 h-4 text-indigo-600" />
          <span className="font-medium text-sm">Open in Maps</span>
        </a>
      </div>
      
      {/* Venue Details */}
      <div className="space-y-2">
        <h4 className="font-bold text-foreground text-lg">{venueName}</h4>
        <p className="text-muted-foreground">{venue?.district || 'RiNo'}, Denver</p>
        {venue?.address && (
          <p className="text-sm text-muted-foreground">{venue.address}</p>
        )}
        <p className="text-sm text-muted-foreground flex items-center gap-2">
          <span className="text-gray-400">Closest Train Station:</span>
          38th & Blake
        </p>
      </div>
    </div>
  );
};

// Organiser & Venue Cards
const OrganiserVenueCards = ({ venue, event, navigate, user }) => {
  const [contactModalOpen, setContactModalOpen] = useState(false);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" id="section-organiser">
        {/* Organiser Card */}
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Organiser</p>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold">
                {venue?.name?.charAt(0) || event?.venue_name?.charAt(0) || 'O'}
              </div>
              <div>
                <h4 className="font-bold text-foreground">{venue?.name || event?.venue_name}</h4>
                <button 
                  onClick={() => {
                    console.log('Contact Organiser button clicked');
                    setContactModalOpen(true);
                  }}
                  className="text-indigo-600 hover:text-indigo-700 text-sm font-medium flex items-center gap-1 mt-1"
                  data-testid="contact-organiser-btn"
                  type="button"
                >
                  <Mail className="w-3 h-3" />
                  Contact Organiser
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Venue Card */}
        <Card id="section-venue">
        <CardContent className="p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Venue</p>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-gray-500" />
            </div>
            <div>
              {venue?.id ? (
                <>
                  <Link 
                    to={venueUrl(venue)}
                    className="font-bold text-foreground hover:text-indigo-600 transition-colors block"
                    data-testid="view-venue-profile-link"
                  >
                    View Venue Profile
                  </Link>
                  <Link 
                    to={venueUrl(venue)}
                    className="text-indigo-600 hover:text-indigo-700 text-sm font-medium flex items-center gap-1 mt-1"
                    data-testid="see-all-events-link"
                  >
                    See all events
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                </>
              ) : (
                <>
                  <span className="font-bold text-muted-foreground block">
                    {event?.venue_name || 'Venue'}
                  </span>
                  {event?.venue_address && <span className="text-muted-foreground text-sm">{event.venue_address}</span>}
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
    
    {/* Contact Organiser Modal */}
    <ContactOrganiserModal
      isOpen={contactModalOpen}
      onClose={() => setContactModalOpen(false)}
      eventTitle={event?.title || 'Event'}
      eventId={event?.id}
      organiserId={event?.created_by || venue?.partner_id}
      organiserName={venue?.name || event?.venue_name || 'Organiser'}
      userEmail={user?.email || ''}
      userName={user?.full_name || ''}
    />
    </>
  );
};

const EventDetail = () => {
  const { id, customUrl } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isGold, user } = useAuth();
  const [event, setEvent] = useState(null);
  const [venue, setVenue] = useState(null);
  const [relatedEvents, setRelatedEvents] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('gallery');

  useEffect(() => {
    fetchEvent();
  }, [id, customUrl]);

  const fetchEvent = async () => {
    try {
      let eventResponse;
      
      if (customUrl) {
        eventResponse = await api.get(`/events/by-url/${customUrl}`);
      } else {
        eventResponse = await api.get(`/events/${id}`);
      }
      
      setEvent(eventResponse.data);
      
      if (eventResponse.data.venue_id) {
        try {
          const venueResponse = await api.get(`/venues/${eventResponse.data.venue_id}`);
          setVenue(venueResponse.data);
        } catch (e) {
          console.log('Venue not found');
        }
      }
      
      try {
        const eventId = eventResponse.data.id || id;
        const relatedResponse = await api.get(`/events/${eventId}/related`);
        setRelatedEvents(relatedResponse.data);
      } catch (e) {
        console.log('No related events');
      }
      
      try {
        const eventId = eventResponse.data.id || id;
        const reviewsResponse = await api.get('/reviews', {
          params: { target_type: 'event', target_id: eventId }
        });
        setReviews(reviewsResponse.data);
      } catch (e) {
        console.log('No reviews');
      }
    } catch (error) {
      toast.error('Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <ConsumerLayout hideFooter>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
        </div>
      </ConsumerLayout>
    );
  }

  if (!event) {
    return (
      <ConsumerLayout hideFooter>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h1 className="text-3xl font-bold mb-4">Event not found</h1>
          <Button onClick={() => navigate('/events')} data-testid="back-to-events-btn">
            Back to Events
          </Button>
        </div>
      </ConsumerLayout>
    );
  }

  const highlights = event.highlights || ['Live Music', 'Food & Drinks', 'Entertainment'];
  const availableTickets = event.capacity - (event.tickets_sold || 0);
  const isSellingFast = availableTickets < event.capacity * 0.2;
  
  // Navigation tabs with section IDs
  const navTabs = [
    { id: 'gallery', label: 'Gallery', sectionId: 'section-gallery' },
    { id: 'description', label: 'Description', sectionId: 'section-description' },
    { id: 'tickets', label: 'Tickets', sectionId: 'section-tickets' },
    { id: 'location', label: 'Location', sectionId: 'section-location' },
    { id: 'organiser', label: 'Organiser', sectionId: 'section-organiser' },
    { id: 'venue', label: 'Venue', sectionId: 'section-venue' },
  ];
  
  // Scroll to section when tab is clicked
  const handleTabClick = (tab) => {
    setActiveSection(tab.id);
    const element = document.getElementById(tab.sectionId);
    if (element) {
      const headerOffset = 80; // Account for sticky header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  
  // Handle venue link click
  const handleVenueLinkClick = (e) => {
    if (!venue?.id) {
      e.preventDefault();
      toast.info('Venue details coming soon');
    }
  };

  return (
    <ConsumerLayout>

      {/* Compact Hero with Event Title */}
      <div className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button
            variant="ghost"
            className="mb-4 text-white/80 hover:text-white hover:bg-white/10"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          {/* Badges */}
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <Badge className="bg-indigo-600 text-white border-0">
              {event.category?.replace('_', ' ').toUpperCase() || 'EVENT'}
            </Badge>
            {event.gold_exclusive && (
              <Badge className="bg-[#D4AF37] text-black border-0">
                <Star className="w-3 h-3 mr-1 fill-current" />
                GOLD EXCLUSIVE
              </Badge>
            )}
            {isSellingFast && (
              <Badge className="bg-rose-500 text-white border-0">
                <Ticket className="w-3 h-3 mr-1" />
                SELLING FAST
              </Badge>
            )}
          </div>
          
          {/* Title */}
          <h1 className="text-3xl lg:text-4xl font-bold mb-4" data-testid="event-title">
            {event.title}
          </h1>
          
          {/* Event Quick Info & Social Share */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-6 text-white/90">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>{formatDate(event.date)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span>{formatTime(event.time)}</span>
              </div>
              {venue?.id ? (
                <Link 
                  to={venueUrl(venue)}
                  className="flex items-center gap-2 hover:text-white transition-colors"
                  data-testid="venue-link"
                >
                  <MapPin className="w-5 h-5" />
                  <span className="underline">{event.venue_name}</span>
                </Link>
              ) : (
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  <span>{event.venue_name}</span>
                </div>
              )}
            </div>
            
            <SocialShare title={event.title} description={event.description} />
          </div>
        </div>
      </div>

      {/* Section Navigation */}
      <div className="border-b sticky top-0 bg-background z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-1 overflow-x-auto py-2">
            {navTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab)}
                className={`px-4 py-2 text-sm font-medium whitespace-nowrap rounded-lg transition-colors ${
                  activeSection === tab.id
                    ? 'bg-indigo-100 dark:bg-indigo-950 text-indigo-600'
                    : 'text-muted-foreground hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
                data-testid={`nav-tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Gallery Section - Always visible first */}
            <div id="section-gallery">
              <GallerySection images={event.images} title={event.title} />
            </div>
            
            {/* About This Event */}
            <div className="space-y-4" id="section-description">
              <h3 className="text-lg font-bold text-foreground">About This Event</h3>
              <p className="text-muted-foreground leading-relaxed">
                {event.description}
              </p>
            </div>
            
            {/* Highlights */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-foreground">Highlights</h3>
              <ul className="space-y-2">
                {highlights.map((highlight, index) => {
                  const Icon = HIGHLIGHT_ICONS[highlight] || PartyPopper;
                  return (
                    <li key={index} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-indigo-600" />
                      </div>
                      <span className="text-foreground">{highlight}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
            
            {/* Location Section */}
            <div id="section-location">
              <LocationSection venue={venue} event={event} />
            </div>
            
            {/* Organiser & Venue Cards */}
            <OrganiserVenueCards venue={venue} event={event} navigate={navigate} user={user} />
            
            {/* Related Events */}
            {relatedEvents.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-foreground">More From This Venue</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {relatedEvents.slice(0, 4).map((relEvent) => (
                    <Link
                      key={relEvent.id}
                      to={`/events/${relEvent.id}`}
                      className="flex items-center gap-3 p-3 border rounded-lg hover:border-indigo-600/50 transition-colors group"
                    >
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-gradient-to-br from-indigo-100 to-purple-100 flex-shrink-0">
                        {relEvent.images?.[0] ? (
                          <img src={relEvent.images[0]} alt={relEvent.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Calendar className="w-6 h-6 text-indigo-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-grow min-w-0">
                        <h4 className="font-medium text-foreground group-hover:text-indigo-600 transition-colors truncate">
                          {relEvent.title}
                        </h4>
                        <p className="text-sm text-muted-foreground">{formatDate(relEvent.date)}</p>
                        <p className="text-sm font-bold">${relEvent.price}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Sticky Ticket Widget */}
          <div className="lg:col-span-1" id="section-tickets">
            <div className="sticky top-20">
              <TicketBookingWidget 
                event={event}
                isGold={isGold}
                isAuthenticated={isAuthenticated}
                venue={venue}
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
    </ConsumerLayout>
  );
};

export default EventDetail;
